import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { normalizeRotationDegrees } from '@/lib/transforms/geometry'
import { canvasMinForSingleShapePanel } from '@/lib/shapes/shape-panel-bounds'
import type { ResizeHandle } from '../types/resize-handle'

export function computeLocalPanelResizeDeltas(
  direction: ResizeHandle,
  widthDelta: number,
  heightDelta: number
): { dx: number; dy: number } {
  const dx = direction.includes('e') ? widthDelta : direction.includes('w') ? -widthDelta : 0
  const dy = direction.includes('s') ? heightDelta : direction.includes('n') ? -heightDelta : 0

  return { dx, dy }
}

export function scorePanelResizeDirection(params: {
  shape: ShapeData
  candidate: { x: number; y: number; width: number; height: number }
}): number {
  const { shape, candidate } = params
  const basePanelMin = canvasMinForSingleShapePanel(shape)
  const candidatePanelMin = canvasMinForSingleShapePanel({
    ...shape,
    x: candidate.x,
    y: candidate.y,
    width: candidate.width,
    height: candidate.height,
  })
  const dx = Math.abs(candidatePanelMin.minX - basePanelMin.minX)
  const dy = Math.abs(candidatePanelMin.minY - basePanelMin.minY)

  // Minimize motion in the panel-visible X/Y basis (rotated frame min corner).
  return dx + dy
}

export function preferredPanelResizeAxes(rotationDeg: number): {
  horizontal: 'e' | 'w'
  vertical: 'n' | 's'
} {
  const rad = (normalizeRotationDegrees(rotationDeg) * Math.PI) / 180
  const cos = Math.cos(rad)
  const EPS = 1e-6

  return {
    horizontal: cos > EPS ? 'e' : 'w',
    vertical: cos > EPS ? 's' : 'n',
  }
}

export function orderedPanelResizeCandidates(
  field: 'width' | 'height',
  linked: boolean,
  rotationDeg: number
): ResizeHandle[] {
  const axes = preferredPanelResizeAxes(rotationDeg)
  const oppositeHorizontal: 'e' | 'w' = axes.horizontal === 'e' ? 'w' : 'e'
  const oppositeVertical: 'n' | 's' = axes.vertical === 'n' ? 's' : 'n'

  if (!linked) {
    return field === 'width'
      ? [axes.horizontal, oppositeHorizontal]
      : [axes.vertical, oppositeVertical]
  }

  return [
    `${axes.vertical}${axes.horizontal}`,
    `${axes.vertical}${oppositeHorizontal}`,
    `${oppositeVertical}${axes.horizontal}`,
    `${oppositeVertical}${oppositeHorizontal}`,
  ] as ResizeHandle[]
}
